import ArrowDropDown from '@mui/icons-material/ArrowDropDown';
import ArrowDropUp from '@mui/icons-material/ArrowDropUp';
import CancelIcon from '@mui/icons-material/Cancel';
import { Grid, ListItemButton, Popover } from '@mui/material';
import React, { memo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { darkGray } from '../../../../../theme/color-constants';
import { useFilterValues } from '../../../../api/data/hooks';
import { UNDEFINED_DISPLAY_KEY } from '../../../../constants';
import { FilterPill } from './FilterPill';
import { FilterGroup, FilterTypes } from './types';
import { isStaleFilter } from './utils';

const StyledButton = styled(ListItemButton)<{
  $open: boolean;
  $chipColor: string;
}>`
  padding: 2px;
  flex-grow: unset;
  color: ${(props) => props.$chipColor};
  margin: 5px;
  border: 1px solid ${(props) => props.$chipColor};
  border-radius: 20px;

  & :hover {
    border-radius: ${(props) => (props.$open ? '20px 20px 0px 0px' : '20px')};
  }
`;

const StyledCancelIcon = styled(CancelIcon)`
  & :hover {
    color: ${darkGray};
  }
`;

interface FilterPillsGroupProps<T extends FilterTypes> {
  filterGroup: FilterGroup<T>;
  removeFilter: (filter: T) => void;
  removeFilters: (filters: T[]) => void;
}

export const FilterPillsGroup = <T extends FilterTypes>(
  props: FilterPillsGroupProps<T>
) => {
  const { t } = useTranslation();
  const { filterGroup, removeFilter, removeFilters } = props;
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  // TODO: bring back the filter number when the CBP have discussed whether we want it or not
  // If we keep it we have to make it reactive to the timeslider range
  // const { data: hierarchyItem } = useFilterValues(filterGroup.filterDataManager);
  // const totalHierarchyLength = useMemo(() => filterGroup.filterDataManager.count(hierarchyItem), [hierarchyItem]);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const id = open ? 'simple-popper' : undefined;

  const { filterDataManager } = filterGroup;
  const { data: hierarchyItem } = useFilterValues(filterDataManager);
  return (
    <>
      <StyledButton
        aria-owns={open ? 'mouse-over-popover' : undefined}
        aria-haspopup="true"
        aria-describedby={id}
        $chipColor={filterGroup.color}
        $open={open}
      >
        <Grid onClick={handleClick} container>
          {open ? <ArrowDropUp /> : <ArrowDropDown />}
          <Grid item>{filterGroup.label}</Grid>
          {/*<Grid item sx={{ color: darkGray }}>{` (${filterGroup.filters.length}/${totalHierarchyLength})`}</Grid>*/}
        </Grid>
        <StyledCancelIcon
          onClick={() =>
            removeFilters(filterGroup.filters.map((f) => f.originalFilter))
          }
        />
      </StyledButton>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        onClose={handleClose}
        disableRestoreFocus
      >
        <div style={{ maxHeight: '50vh' }}>
          {filterGroup.filters.map((filter) => {
            const isStale = isStaleFilter(filter.originalFilter, hierarchyItem);
            return (
              <FilterPill
                key={filter.key}
                label={filter.originalFilter.label ?? t(UNDEFINED_DISPLAY_KEY)}
                color={isStale ? 'grey' : filter.color}
                onDelete={
                  filter.allowDelete
                    ? () => {
                        removeFilter(filter.originalFilter);
                      }
                    : undefined
                }
              />
            );
          })}
        </div>
      </Popover>
    </>
  );
};
FilterPillsGroup.displayName = 'FilterPillsGroup';

export const FilterPillsGroupMemo = memo(
  FilterPillsGroup,
  <T extends FilterTypes>(
    oldProps: FilterPillsGroupProps<T>,
    newProps: FilterPillsGroupProps<T>
  ) => {
    return oldProps.filterGroup.filters.every(
      (f, i) => f.key === newProps.filterGroup.filters[i].key
    );
  }
) as typeof FilterPillsGroup;
FilterPillsGroupMemo.displayName = 'FilterPillsGroupMemo';
